import { createContext, useContext, useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from './AuthContext';
import { estimateFuelConsumption } from '../utils/fuelConsumption';

interface Shift {
  id: string;
  user_id: string;
  status: 'active' | 'finished';
  started_at: string;
  finished_at: string | null;
  total_deliveries: number;
  total_earnings: number;
  total_distance: number;
  total_duration: number;
  vehicle_type: string | null;
  vehicle_model: string | null;
  avg_speed: number;
  avg_fuel_consumption: number;
  created_at: string;
}

interface StartShiftData {
  vehicle_type: string;
  vehicle_model: string;
}

interface ShiftContextType {
  activeShift: Shift | null;
  shiftHistory: Shift[];
  loading: boolean;
  elapsedTime: number;
  startShift: (data: StartShiftData) => Promise<Shift | null>;
  finishShift: () => Promise<Shift | null>;
  fetchShifts: () => Promise<void>;
}

const ShiftContext = createContext<ShiftContextType | undefined>(undefined);

export function ShiftProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const [activeShift, setActiveShift] = useState<Shift | null>(null);
  const [shiftHistory, setShiftHistory] = useState<Shift[]>([]);
  const [loading, setLoading] = useState(true);
  const [elapsedTime, setElapsedTime] = useState(0);
  const isFetching = useRef(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Timer para contar tempo do turno ativo
  useEffect(() => {
    if (activeShift) {
      const startTime = new Date(activeShift.started_at).getTime();

      const updateElapsed = () => {
        const now = Date.now();
        setElapsedTime(Math.floor((now - startTime) / 1000));
      };

      updateElapsed();
      timerRef.current = setInterval(updateElapsed, 1000);

      return () => {
        if (timerRef.current) clearInterval(timerRef.current);
      };
    } else {
      setElapsedTime(0);
      if (timerRef.current) clearInterval(timerRef.current);
    }
  }, [activeShift]);

  const fetchShifts = useCallback(async () => {
    if (!user || isFetching.current) return;
    isFetching.current = true;

    try {
      const { data, error } = await supabase
        .from('shifts')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;

      const shifts = data || [];
      const active = shifts.find((s: Shift) => s.status === 'active') || null;
      const history = shifts.filter((s: Shift) => s.status === 'finished');

      setActiveShift(active);
      setShiftHistory(history);
    } catch (error) {
      console.error('Erro ao buscar turnos:', error);
    } finally {
      isFetching.current = false;
    }
  }, [user]);

  const startShift = useCallback(async (data: StartShiftData) => {
    if (!user || activeShift) return null;

    try {
      const { data: shiftData, error } = await supabase
        .from('shifts')
        .insert({
          user_id: user.id,
          status: 'active',
          started_at: new Date().toISOString(),
          vehicle_type: data.vehicle_type,
          vehicle_model: data.vehicle_model,
        })
        .select()
        .single();

      if (error) throw error;
      setActiveShift(shiftData);
      return shiftData;
    } catch (error) {
      console.error('Erro ao iniciar turno:', error);
      return null;
    }
  }, [user, activeShift]);

  const finishShift = useCallback(async () => {
    if (!user || !activeShift) return null;

    const now = new Date().toISOString();
    const startTime = new Date(activeShift.started_at).getTime();
    const endTime = new Date(now).getTime();
    const durationSeconds = Math.round((endTime - startTime) / 1000);

    // Buscar entregas feitas durante o turno
    try {
      const { data: shiftDeliveries, error: delError } = await supabase
        .from('deliveries')
        .select('*')
        .eq('user_id', user.id)
        .eq('status', 'completed')
        .gte('created_at', activeShift.started_at)
        .lte('created_at', now);

      if (delError) throw delError;

      const totalDeliveries = shiftDeliveries?.length || 0;
      const totalEarnings = shiftDeliveries?.reduce((sum: number, d: any) => sum + (d.amount || 0), 0) || 0;
      const totalDistance = shiftDeliveries?.reduce((sum: number, d: any) => sum + (d.total_distance || 0), 0) || 0;

      // Calcular tempo real em trânsito (soma das durações das rotas/entregas)
      const totalRouteDuration = shiftDeliveries?.reduce((sum: number, d: any) => sum + (d.total_duration || 0), 0) || 0;

      // Estimar consumo de combustível
      const fuelEstimate = estimateFuelConsumption(
        activeShift.vehicle_type || 'carro',
        activeShift.vehicle_model || '',
        totalDistance,
        totalRouteDuration > 0 ? totalRouteDuration : durationSeconds,
      );

      const { data, error } = await supabase
        .from('shifts')
        .update({
          status: 'finished',
          finished_at: now,
          total_deliveries: totalDeliveries,
          total_earnings: totalEarnings,
          total_distance: totalDistance,
          total_duration: durationSeconds,
          avg_speed: fuelEstimate.avgSpeed,
          avg_fuel_consumption: fuelEstimate.avgConsumption,
        })
        .eq('id', activeShift.id)
        .eq('user_id', user.id)
        .select()
        .single();

      if (error) throw error;

      setActiveShift(null);
      setShiftHistory((prev) => [data, ...prev]);
      return { ...data, _fuelEstimate: fuelEstimate };
    } catch (error) {
      console.error('Erro ao finalizar turno:', error);
      await fetchShifts();
      return null;
    }
  }, [user, activeShift, fetchShifts]);

  useEffect(() => {
    if (!user) {
      setLoading(false);
      setActiveShift(null);
      setShiftHistory([]);
      return;
    }

    const load = async () => {
      setLoading(true);
      await fetchShifts();
      setLoading(false);
    };
    load();
  }, [user, fetchShifts]);

  const value = useMemo(() => ({
    activeShift,
    shiftHistory,
    loading,
    elapsedTime,
    startShift,
    finishShift,
    fetchShifts,
  }), [activeShift, shiftHistory, loading, elapsedTime, startShift, finishShift, fetchShifts]);

  return (
    <ShiftContext.Provider value={value}>
      {children}
    </ShiftContext.Provider>
  );
}

export function useShiftContext() {
  const context = useContext(ShiftContext);
  if (context === undefined) {
    throw new Error('useShiftContext deve ser usado dentro de um ShiftProvider');
  }
  return context;
}
